import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MapPin, Minus, Plus, Trash2, X } from "lucide-react";
import axios from "axios";
import { useCart } from "@/context/CartContext";
import { BRANDS } from "@/data/products";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TAX_RATE = 0.0725;
const FREE_DELIVERY = 25;
const BULK_FREE_UNITS = 6;
const DELIVERY_FEE = 5;
const SQUARE_APP_ID = process.env.REACT_APP_SQUARE_APPLICATION_ID;
const SQUARE_LOCATION_ID = process.env.REACT_APP_SQUARE_LOCATION_ID;
const squareReady = Boolean(SQUARE_APP_ID && SQUARE_LOCATION_ID);

export const CartDrawer = () => {
  const { items, setQty, remove, clear, subtotal, open, setOpen } = useCart();
  const [fulfillment, setFulfillment] = useState("pickup");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [placing, setPlacing] = useState(false);
  const [orderId, setOrderId] = useState(null);
  const [cardReady, setCardReady] = useState(false);
  const [payError, setPayError] = useState("");
  const [address, setAddress] = useState("");
  const [zone, setZone] = useState("idle");
  const [zoneMsg, setZoneMsg] = useState("");
  const [zonePoint, setZonePoint] = useState(null);
  const cardRef = useRef(null);
  const cardAttached = useRef(false);

  const tax = subtotal * TAX_RATE;
  const lemonadeUnits = items.filter((p) => p.brand === "lemonade").reduce((s, p) => s + p.qty, 0);
  const freeDelivery = subtotal >= FREE_DELIVERY || lemonadeUnits >= BULK_FREE_UNITS;
  const deliveryFee = fulfillment === "delivery" && !freeDelivery ? DELIVERY_FEE : 0;
  const total = subtotal + tax + deliveryFee;
  const zoneBlocked = fulfillment === "delivery" && zone !== "ok";

  useEffect(() => {
    if (!open || !squareReady || items.length === 0 || orderId || cardAttached.current) return;
    if (!window.Square) return;
    let cancelled = false;
    (async () => {
      try {
        const payments = window.Square.payments(SQUARE_APP_ID, SQUARE_LOCATION_ID);
        const card = await payments.card();
        await card.attach("#gb-card-container");
        if (!cancelled) {
          cardRef.current = card;
          cardAttached.current = true;
          setCardReady(true);
        }
      } catch {
        if (!cancelled) setPayError("Card form could not load — refresh and try again");
      }
    })();
    return () => { cancelled = true; };
  }, [open, items.length, orderId]);

  const destroyCard = () => {
    if (cardRef.current) {
      cardRef.current.destroy?.().catch(() => {});
      cardRef.current = null;
    }
    cardAttached.current = false;
    setCardReady(false);
  };

  const checkZone = async () => {
    if (!address.trim()) return;
    setZone("checking");
    setZoneMsg("");
    try {
      const { data } = await axios.post(`${API}/delivery/check`, { address });
      setZonePoint(data.lat && data.lon ? { lat: data.lat, lon: data.lon } : null);
      if (data.skipped) {
        setZone("ok");
        setZoneMsg("Delivery available — we'll confirm your address by text.");
      } else if (data.ok) {
        setZone("ok");
        setZoneMsg(`${data.miles} mi away — inside our 5-mile delivery zone`);
      } else {
        setZone("fail");
        setZoneMsg(`${data.miles} mi away — outside our 5-mile delivery zone. Choose pickup or text us for options.`);
      }
    } catch (e) {
      setZone("fail");
      setZoneMsg(e.response?.data?.detail || "Couldn't check that address — try again");
    }
  };

  const pay = async () => {
    if (!name.trim() || !phone.trim()) {
      toast.error("Add your name and phone so we can confirm your order");
      return;
    }
    if (!cardRef.current) {
      setPayError("Card form not ready yet — one second");
      return;
    }
    setPlacing(true);
    setPayError("");
    try {
      const tok = await cardRef.current.tokenize({
        amount: total.toFixed(2),
        currencyCode: "USD",
        intent: "CHARGE",
        customerInitiated: true,
        sellerKeyedIn: false,
        billingContact: { givenName: name.trim(), phone: phone.trim(), countryCode: "US" },
      });
      if (tok.status !== "OK") {
        throw new Error(tok.errors?.[0]?.message || "Card could not be verified");
      }
      const { data } = await axios.post(`${API}/payments/checkout`, {
        customer_name: name,
        phone,
        fulfillment,
        address: fulfillment === "delivery" ? address.trim() : null,
        source_id: tok.token,
        idempotency_key: crypto.randomUUID(),
        items: items.map((p) => ({ id: p.id, qty: p.qty, variant: p.variant || "", addons: p.addons || [] })),
      });
      if (data.status === "paid") {
        setOrderId(data.order_id);
        clear();
        destroyCard();
        toast.success("Payment successful — order confirmed!");
      } else {
        setPayError("Payment is pending — we'll confirm it shortly.");
      }
    } catch (e) {
      setPayError(e.response?.data?.detail || e.message || "Payment failed — try again");
    } finally {
      setPlacing(false);
    }
  };

  const close = () => {
    setOpen(false);
    setOrderId(null);
    setPayError("");
    destroyCard();
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            data-testid="cart-overlay"
            className="fixed inset-0 z-[95] bg-black/70"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={close}
          />
          <motion.aside
            data-testid="cart-drawer"
            className="fixed right-0 top-0 z-[100] h-full w-full sm:w-[420px] bg-[#111] border-l border-white/10 flex flex-col"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
          >
            <div className="flex items-center justify-between px-6 py-5 border-b border-white/10">
              <h2 className="font-extrabold text-lg text-white">Your Bag</h2>
              <button data-testid="cart-close-button" onClick={close} className="text-zinc-400 hover:text-white transition-colors" aria-label="Close cart">
                <X className="w-5 h-5" />
              </button>
            </div>

            {orderId ? (
              <div className="flex-1 flex flex-col items-center justify-center gap-4 px-8 text-center" data-testid="order-confirmation">
                <span className="w-14 h-14 rounded-full bg-emerald-400/15 border border-emerald-400/40 flex items-center justify-center text-emerald-300 text-2xl">✓</span>
                <h3 className="font-extrabold text-xl text-white">Payment Confirmed</h3>
                <p className="text-sm text-zinc-400 leading-relaxed">
                  Order <span className="text-amber-300 font-semibold">#{orderId.slice(0, 8)}</span> is paid.
                  We'll text <span className="text-zinc-200">{phone}</span> to confirm {fulfillment} details.
                </p>
                <button data-testid="order-continue-button" onClick={close} className="mt-2 rounded-full bg-white text-black text-sm font-bold px-6 py-2.5">
                  Keep Shopping
                </button>
              </div>
            ) : items.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center gap-3 px-8 text-center" data-testid="cart-empty-state">
                <p className="font-extrabold text-lg text-zinc-300">Your bag is empty</p>
                <p className="text-sm text-zinc-500">Add some bling, glow, or a cold 34oz pour.</p>
              </div>
            ) : (
              <>
                <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3" data-testid="cart-items-list">
                  {items.map((p) => (
                    <div key={p.key} className="flex gap-3 items-center rounded-xl border border-white/10 bg-[#181818] p-3" data-testid={`cart-item-${p.id}`}>
                      <img src={p.image} alt={p.name} className="w-14 h-14 rounded-lg object-cover" />
                      <div className="flex-1 min-w-0">
                        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-pink-400">
                          {BRANDS[p.brand].short}
                        </p>
                        <p className="text-sm font-semibold text-white truncate">{p.name}</p>
                        {p.variant && <p className="text-xs text-zinc-500 truncate">{p.variant}</p>}
                        <p className="text-sm text-amber-300 font-bold">${(p.price * p.qty).toFixed(2)}</p>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <button data-testid={`cart-remove-${p.id}`} onClick={() => remove(p.key)} className="text-zinc-600 hover:text-red-400 transition-colors" aria-label={`Remove ${p.name}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                        <div className="flex items-center gap-2">
                          <button data-testid={`cart-qty-minus-${p.id}`} onClick={() => setQty(p.key, p.qty - 1)} className="w-6 h-6 rounded-full border border-white/15 text-zinc-300 flex items-center justify-center hover:border-white/50" aria-label="Decrease quantity">
                            <Minus className="w-3 h-3" />
                          </button>
                          <span className="text-sm w-4 text-center text-white" data-testid={`cart-qty-${p.id}`}>{p.qty}</span>
                          <button data-testid={`cart-qty-plus-${p.id}`} onClick={() => setQty(p.key, p.qty + 1)} className="w-6 h-6 rounded-full border border-white/15 text-zinc-300 flex items-center justify-center hover:border-white/50" aria-label="Increase quantity">
                            <Plus className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="border-t border-white/10 px-6 py-5 space-y-4 overflow-y-auto max-h-[58vh]">
                  <div className="grid grid-cols-2 gap-2" data-testid="fulfillment-toggle">
                    {["pickup", "delivery"].map((f) => (
                      <button
                        key={f}
                        data-testid={`fulfillment-${f}-button`}
                        onClick={() => { setFulfillment(f); setZone("idle"); setZoneMsg(""); }}
                        className={`rounded-full border px-4 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${
                          fulfillment === f ? "border-pink-500 bg-pink-500/15 text-pink-300" : "border-white/15 text-zinc-500"
                        }`}
                      >
                        {f === "pickup" ? "CLT Pickup · Free" : "Delivery ≤5 mi · $5"}
                      </button>
                    ))}
                  </div>
                  {fulfillment === "delivery" && (
                    <>
                      {freeDelivery ? (
                        <p className="text-xs text-emerald-300" data-testid="free-delivery-unlocked">🎉 Free delivery unlocked</p>
                      ) : (
                        <p className="text-xs text-zinc-500" data-testid="free-delivery-hint">
                          Free delivery (≤5 mi) at <span className="text-amber-300">$25+</span> or <span className="text-amber-300">6+ drinks</span>
                          {" "}— add ${(FREE_DELIVERY - subtotal).toFixed(2)} more{lemonadeUnits < BULK_FREE_UNITS ? ` or ${BULK_FREE_UNITS - lemonadeUnits} more drink${BULK_FREE_UNITS - lemonadeUnits === 1 ? "" : "s"}` : ""}
                        </p>
                      )}
                      <div className="flex gap-2">
                        <input
                          data-testid="delivery-address-input"
                          value={address}
                          onChange={(e) => { setAddress(e.target.value); setZone("idle"); setZoneMsg(""); }}
                          placeholder="Delivery address (street, zip)"
                          className="flex-1 rounded-lg bg-black/40 border border-white/15 px-3 py-2.5 text-sm text-white placeholder:text-zinc-600 focus:border-pink-400 focus:outline-none"
                        />
                        <button
                          data-testid="delivery-check-button"
                          onClick={checkZone}
                          disabled={zone === "checking" || !address.trim()}
                          className="rounded-lg bg-pink-500 text-white text-xs font-bold px-4 disabled:opacity-40 inline-flex items-center gap-1.5"
                        >
                          <MapPin className="w-3.5 h-3.5" /> {zone === "checking" ? "…" : "Check"}
                        </button>
                      </div>
                      {zone === "ok" && <p className="text-xs text-emerald-300" data-testid="delivery-zone-ok">✓ {zoneMsg}</p>}
                      {zone === "ok" && zonePoint && (
                        <iframe
                          title="Delivery address map"
                          data-testid="delivery-map"
                          src={`https://www.openstreetmap.org/export/embed.html?bbox=${zonePoint.lon - 0.02},${zonePoint.lat - 0.013},${zonePoint.lon + 0.02},${zonePoint.lat + 0.013}&layer=mapnik&marker=${zonePoint.lat},${zonePoint.lon}`}
                          className="w-full h-36 rounded-lg border border-white/15"
                        />
                      )}
                      {zone === "fail" && <p className="text-xs text-red-400" data-testid="delivery-zone-fail">✗ {zoneMsg}</p>}
                    </>
                  )}
                  <div className="space-y-1.5 text-sm" data-testid="cart-totals">
                    <div className="flex justify-between text-zinc-400"><span>Subtotal</span><span>${subtotal.toFixed(2)}</span></div>
                    <div className="flex justify-between text-zinc-400"><span>NC sales tax</span><span>${tax.toFixed(2)}</span></div>
                    <div className="flex justify-between text-zinc-400"><span>{fulfillment === "delivery" ? "Delivery" : "Pickup"}</span><span>{deliveryFee === 0 ? "Free" : `$${deliveryFee.toFixed(2)}`}</span></div>
                    <div className="flex justify-between text-white font-bold pt-2 border-t border-white/10">
                      <span>Total</span><span className="text-amber-300" data-testid="cart-total">${total.toFixed(2)}</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      data-testid="checkout-name-input"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Your name"
                      className="rounded-lg bg-black/40 border border-white/15 px-3 py-2.5 text-sm text-white placeholder:text-zinc-600 focus:border-pink-400 focus:outline-none"
                    />
                    <input
                      data-testid="checkout-phone-input"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="Phone"
                      className="rounded-lg bg-black/40 border border-white/15 px-3 py-2.5 text-sm text-white placeholder:text-zinc-600 focus:border-pink-400 focus:outline-none"
                    />
                  </div>
                  <div className="rounded-lg border border-white/15 bg-black/40 p-2 min-h-[90px]" id="gb-card-container" data-testid="square-card-container" />
                  {payError && <p className="text-sm text-red-400" data-testid="payment-error">{payError}</p>}
                  {zoneBlocked && !payError && (
                    <p className="text-xs text-zinc-500 text-center" data-testid="zone-required-hint">Enter your delivery address and tap Check to continue</p>
                  )}
                  <button
                    data-testid="checkout-submit-button"
                    onClick={pay}
                    disabled={placing || !cardReady || zoneBlocked}
                    className="w-full rounded-full bg-white text-black font-bold text-sm py-3.5 transition-colors hover:bg-zinc-200 disabled:opacity-50"
                  >
                    {placing ? "Processing…" : `Pay $${total.toFixed(2)}`}
                  </button>
                  <p className="text-center text-[10px] uppercase tracking-[0.2em] text-zinc-600">
                    Secure checkout by Square · Card · Afterpay · Cash App · Apple Pay · Google Pay
                  </p>
                </div>
              </>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};

export default CartDrawer;
